import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';
import axiosInstance from '../../../Components/Request';
import { useDebounce } from '../../../Hooks/useDebounce';
import {
  EMPTY_FILTERS,
  EMPTY_FILTER_OPTIONS,
  LoadsFilterOptions,
  LoadsFilters,
  describeActiveFilters,
  filtersToParams,
  monthLabel,
} from './loadsFilters';

/** Los dos reportes de terrestre: el de siempre y el de incidencias del Protect Time (PENDIENTES_TERRESTRE, punto 8). */
export type ReportKind = 'loads' | 'incidents';

const REPORTS: Record<ReportKind, { path: string; file: string }> = {
  loads: { path: '/generate-report', file: 'land-loads' },
  incidents: { path: '/generate-report/incidents', file: 'land-incidents' },
};

/**
 * El motivo de una descarga rechazada.
 *
 * ⚠️ Con `responseType: 'blob'` axios entrega también el **error** como `Blob`, así que `response.data.message` no
 * existe: hay que leer el texto y parsearlo. Si no es JSON, se usa el texto de reserva.
 */
const blobErrorMessage = async (err: any, fallback: string): Promise<string> => {
  const data = err?.response?.data;
  try {
    if (data && typeof data.text === 'function') {
      const parsed = JSON.parse(await data.text());
      if (parsed?.message) return String(parsed.message);
    } else if (data?.message) {
      return String(data.message);
    }
  } catch {
    /* no era JSON: se queda el de reserva */
  }
  return fallback;
};

export interface LoadRow {
  id: string;
  pro_number?: string;
  operation_type?: string;
  origin?: string;
  destiny?: string;
  mode_name?: string;
  trailer?: string;
  created_date?: string | null;
  protectDate?: string | null;
  status?: { name?: string };
  carrier?: { name?: string };
  loadOffice?: { name?: string };
}

/** Conteo de status devuelto por el backend: { Estatus, count } */
interface StatusCount {
  Estatus: string;
  count: number;
}

interface CatalogRow {
  id: string;
  name: string;
}

export interface UseLoadsDataParams {
  page: number;
  pageSize: number;
  /** `true` = solo cargas críticas (pestaña "Critical Loads"). */
  critical: boolean;
  selectedStatuses: string[];
  filters: LoadsFilters;
}

export const useLoadsData = (params: UseLoadsDataParams) => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const [loads, setLoads] = useState<LoadRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [criticalCount, setCriticalCount] = useState(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  /** Qué reporte se está generando, o `null`. */
  const [generating, setGenerating] = useState<ReportKind | null>(null);
  const [options, setOptions] = useState<LoadsFilterOptions>(EMPTY_FILTER_OPTIONS);

  const debouncedSearch = useDebounce(search, 400);

  const { page, pageSize, critical, selectedStatuses, filters } = params;

  /**
   * Parámetros que comparten la tabla y los conteos.
   *
   * 🔑 **Tienen que ser los mismos, y de ahí que se armen una sola vez.** Las tarjetas de estatus son
   * el resumen de lo que la tabla muestra; si contaran con otros filtros, la pantalla diría "12
   * cargas" arriba y listaría 3 abajo. Antes se evitaba porque los conteos se calculaban en el
   * navegador sobre el mismo arreglo — al pasar al servidor, mantenerlos alineados es explícito.
   */
  const sharedParams = useMemo(
    () => ({
      search: debouncedSearch,
      ...filtersToParams(filters),
      selectedStatuses: selectedStatuses.length ? selectedStatuses : undefined,
      critical: critical ? 'true' : undefined,
    }),
    [debouncedSearch, filters, selectedStatuses, critical]
  );

  /**
   * Firma estable para las dependencias del efecto.
   *
   * ⚠️ `sharedParams` es un objeto nuevo en cada render, así que usarlo como dependencia dispararía
   * la petición sin parar. Se compara su contenido.
   *
   * 📌 `JSON.stringify` alcanza aquí porque son cadenas y arreglos de cadenas — no hay símbolos ni
   * fechas, que es donde este atajo falla.
   */
  const paramsKey = JSON.stringify(sharedParams);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      /**
       * Las tarjetas se piden **sin** `selectedStatuses`, a propósito.
       *
       * 🔑 Son el selector de estatus: si se descontaran, al elegir una las demás caerían a cero y ya
       * no se podría comparar ni cambiar de selección. El resto de los filtros sí aplican.
       */
      const { selectedStatuses: _omit, ...countParams } = sharedParams;

      const [tableRes, statusRes] = await Promise.all([
        axiosInstance.get('/loads-table', {
          params: { ...sharedParams, page, limit: pageSize },
        }),
        axiosInstance.get('/status-loads-cards', { params: countParams }),
      ]);

      /**
       * El endpoint responde el arreglo pelón cuando no se pide página, y un envoltorio cuando sí.
       * Se toleran las dos formas: si el backend se despliega sin la paginación, la pantalla sigue
       * funcionando con la primera página en lugar de quedarse vacía.
       */
      const body = tableRes.data;
      if (Array.isArray(body)) {
        setLoads(body);
        setTotal(body.length);
        setTotalPages(1);
      } else {
        setLoads(body?.data || []);
        setTotal(Number(body?.total) || 0);
        setTotalPages(Math.max(1, Number(body?.pages) || 1));
      }

      const counts: Record<string, number> = {};
      (statusRes.data as StatusCount[]).forEach((s) => {
        counts[s.Estatus] = s.count;
      });
      setStatusCounts(counts);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401) navigateRef.current('/login');
      setError(err?.response?.data?.message || 'Error loading loads');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Contador de la pestaña "Critical Loads".
   *
   * 📌 Se pide aparte y con `limit=1`: solo interesa el `total`, no las filas. Traerlas para
   * contarlas era justo lo que se quería dejar de hacer.
   *
   * 📌 Lleva los filtros del modal y la búsqueda, pero **no** `critical` ni los estatus
   * seleccionados: el número de la pestaña tiene que ser el mismo estando en ella o fuera, o al
   * entrar cambiaría solo.
   */
  useEffect(() => {
    let alive = true;
    const { critical: _c, selectedStatuses: _s, ...rest } = sharedParams;
    axiosInstance
      .get('/loads-table', { params: { ...rest, critical: 'true', page: 1, limit: 1 } })
      .then(({ data }) => {
        if (!alive) return;
        setCriticalCount(Array.isArray(data) ? data.length : Number(data?.total) || 0);
      })
      .catch(() => {
        if (alive) setCriticalCount(0);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  /**
   * Opciones de los menús de filtro.
   *
   * 🔑 Los catálogos (transportistas, oficinas, modos) son la fuente correcta, no los datos
   * cargados: un transportista sin cargas este mes igual debe poder elegirse. Tipos de operación y
   * meses no tienen catálogo, así que los da `/loads-filter-options` con valores distintos de toda la
   * tabla —no de la página— y con el acotado por rol aplicado.
   *
   * 📌 Se pide una sola vez al montar: son catálogos, no cambian mientras alguien filtra.
   */
  useEffect(() => {
    let alive = true;
    const safe = (url: string) => axiosInstance.get(url).then((r) => r.data).catch(() => null);

    Promise.all([
      safe('/carriers'),
      safe('/load-office'),
      safe('/load-modes'),
      safe('/loads-filter-options'),
    ]).then(([carriers, offices, modes, extra]) => {
      if (!alive) return;
      const toOptions = (rows: unknown): { label: string; value: string }[] =>
        Array.isArray(rows)
          ? (rows as CatalogRow[])
              .filter((r) => r?.id && r?.name)
              .map((r) => ({ label: r.name, value: r.id }))
              .sort((a, b) => a.label.localeCompare(b.label))
          : [];

      setOptions({
        carriers: toOptions(carriers),
        offices: toOptions(offices),
        modes: toOptions(modes),
        operationTypes: Array.isArray(extra?.operationTypes)
          ? extra.operationTypes.map((v: string) => ({ label: v, value: v }))
          : [],
        months: Array.isArray(extra?.months)
          ? extra.months.map((k: string) => ({ label: monthLabel(k), value: k }))
          : [],
        /** 19-oct-2026: los clientes asignados con cargas, ya ordenados por nombre en el backend. */
        clients: Array.isArray(extra?.clients)
          ? (extra.clients as CatalogRow[])
              .filter((c) => c?.id && c?.name)
              .map((c) => ({ label: c.name, value: c.id }))
          : [],
      });
    });

    return () => {
      alive = false;
    };
  }, []);

  /**
   * Descarga del reporte de Excel.
   *
   * 🔑 **Lo escribe el backend, ya como `.xlsx`.** Antes el endpoint devolvía CSV, esta función lo
   * parseaba con `papaparse` y lo reescribía con la librería `xlsx`: tres formatos para un archivo, y en
   * el camino se perdían los tipos —las fechas llegaban como texto y Excel las interpretaba según la
   * configuración regional de quien lo abriera—. Además, un CSV no puede llevar el hipervínculo de la
   * columna de enlace público, que es lo que se pidió.
   *
   * 🔑 **Van los mismos parámetros que la tabla** (`sharedParams`): búsqueda, filtros del modal,
   * estatus seleccionados y la pestaña de críticas. El archivo trae exactamente lo que se está viendo.
   * Antes solo se mandaba `search`, así que quien filtraba por oficina y exportaba recibía el universo
   * completo sin ninguna señal de que el filtro se había ignorado.
   *
   * ⚠️ `responseType: 'blob'`. Sin eso, axios interpreta el binario como texto y lo corrompe: el
   * archivo se descarga con un tamaño verosímil y Excel lo rechaza al abrirlo.
   *
   * 📌 **Dos reportes con los mismos filtros** (PENDIENTES_TERRESTRE, punto 8; 13-oct-2026): el de cargas y el de
   * incidencias, que lista cada cambio del Protect Time de esas mismas cargas con su razón. Devuelve `true` o el motivo
   * del fallo, para que el modal lo diga en lugar de quedarse callado.
   */
  const generateReport = useCallback(
    async (kind: ReportKind = 'loads'): Promise<true | string> => {
      setGenerating(kind);
      try {
        const res = await axiosInstance.get(REPORTS[kind].path, {
          params: sharedParams,
          responseType: 'blob',
        });
        const date = new Date().toISOString().slice(0, 10);
        saveAs(new Blob([res.data]), `${REPORTS[kind].file}-${date}.xlsx`);
        return true;
      } catch (err) {
        console.error('Error generating report:', err);
        const message = await blobErrorMessage(err, 'The report could not be generated. Please try again.');
        return message;
      } finally {
        setGenerating(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paramsKey]
  );

  /** Lo que lista el modal de los reportes: armado con lo mismo que viaja (`sharedParams`). */
  const activeFilters = useMemo(
    () =>
      describeActiveFilters({
        search: debouncedSearch,
        selectedStatuses,
        critical,
        filters,
        options,
      }),
    [debouncedSearch, selectedStatuses, critical, filters, options]
  );

  return {
    loads,
    total,
    totalPages,
    statusCounts,
    criticalCount,
    options,
    loading,
    error,
    search,
    setSearch,
    refetch: fetchData,
    generating,
    generateReport,
    activeFilters,
  };
};

export { EMPTY_FILTERS };

/**
 * ¿El load es crítico? (protectDate vencida y no en status final)
 *
 * ⚠️ **La regla de verdad vive en el backend** (`critical` en `loadFilterService`), porque con
 * paginación del lado del servidor esta función solo vería la página actual. Se conserva para pintar
 * el distintivo de una fila ya cargada; **no** se use para contar ni para filtrar.
 */
export const isCriticalLoad = (record: LoadRow): boolean => {
  if (!record.protectDate || record.protectDate === '1970-01-01T00:00:00.000Z')
    return false;
  const statusName = record.status?.name?.toLowerCase();
  if (
    statusName === 'completed' ||
    statusName === 'dry run' ||
    statusName === 'at destination'
  )
    return false;
  return new Date(record.protectDate) < new Date();
};
